import { useEffect, useRef, useState } from "react";

import { getNavigationMap, parseWallClockDate, unauthorizedNavigationMap, useOrganization } from "@asd/customer-portal-ui";
import { Box, CircularProgress, Stack, Toolbar, Typography } from "@mui/material";
import { useDispatch, useStore } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { getScrambledDateString, useScrambler } from "~/providers/ScramblerProvider";
import { ConfigurationService } from "~/services";
import { setConfiguration } from "~/store/configurationSlice";
import { env } from "~/utils/env";
import { DRAWER_WIDTH_CLOSED } from "../pages/Navigation";

import { useKeycloak } from "./";

const ProtectedRoute = ({ children = null }: { children?: any }) => {
  const store = useStore();
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    configuration: {
      ppt: { showCareProviders: showCareProvidersForPPT },
    },
  } = useOrganization();

  const { isScramblerEnabled } = useScrambler();
  const keycloak = useKeycloak();

  const [isLoading, setIsLoading] = useState(Object.keys(keycloak).length === 0);
  const [error, setError] = useState<string | null>(null);

  const wasScramblerEnabled = useRef<boolean>(isScramblerEnabled);
  const configurationPromise = useRef<Promise<void> | null>(null);
  const configurationAbortController = useRef<AbortController>(new AbortController());

  const isAllowed = (
    keycloak.authenticated
      ? getNavigationMap({
          roles: keycloak.realmAccess?.roles ?? [],
          groups: keycloak.userInfo?.groups ?? [],
          showCareProvidersForPPT,
          withProviderRegistry: env.REACT_APP_WITH_PROVIDER_REGISTRY !== "false",
          withProviderDirectory: env.REACT_APP_WITH_PROVIDER_DIRECTORY !== "false",
          withMessages: env.REACT_APP_WITH_MESSAGES !== "false",
          withReports: env.REACT_APP_WITH_REPORTS !== "false",
        })
      : unauthorizedNavigationMap
  )
    .flat()
    .some(({ route }) => route === location.pathname);

  useEffect(
    () => () => {
      configurationAbortController.current?.abort();
      configurationAbortController.current = new AbortController();
    },
    [],
  );

  useEffect(() => {
    if (
      wasScramblerEnabled.current === isScramblerEnabled &&
      (!isAllowed ||
        !keycloak.authenticated ||
        (store.getState() as { ConfigurationSlice: { isInitialized: boolean } }).ConfigurationSlice.isInitialized)
    ) {
      setIsLoading(false);
      return;
    }

    wasScramblerEnabled.current = isScramblerEnabled;
    setIsLoading(true);
    setError(null);
    const thisPromise = ConfigurationService.getConfigurations(undefined, {
      signal: configurationAbortController.current?.signal,
    })
      .then((result) => {
        if (thisPromise === configurationPromise.current) {
          if (!result || !result.data) {
            throw new Error("");
          }

          setError(null);
          dispatch(
            setConfiguration({
              isInitialized: true,
              ...result.data,
              minDate: result.data.minDate ? parseWallClockDate(result.data.minDate).valueOf() : null,
              maxDate: result.data.maxDate ? parseWallClockDate(result.data.maxDate).valueOf() : null,
              payTypes: result.data.payTypes,
              ...(!!isScramblerEnabled && {
                lastPayDate: getScrambledDateString(result.data.lastPayDate),
                participant: {
                  ...result.data.participant,
                  firstName: "Mickey",
                  lastName: "Mouse",
                  lastFourSsn: "0000",
                },
              }),
            }),
          );
        }
      })
      .catch(() => {
        setError("Failed to load configuration data. Please try again later");
      })
      .finally(() => {
        if (thisPromise === configurationPromise.current) {
          setIsLoading(false);
        }
      });

    configurationPromise.current = thisPromise;
  }, [dispatch, isAllowed, store, keycloak.authenticated, isScramblerEnabled]);

  if (isLoading || error) {
    return (
      <Stack direction="column" overflow="hidden" position="absolute" sx={{ inset: 0 }}>
        <Toolbar variant="dense" />
        <Box
          sx={(theme) => ({
            width: "100%",
            height: "100%",
            position: "relative",
            alignContent: "center",
            textAlign: "center",
            left: DRAWER_WIDTH_CLOSED,
            background: theme.palette.background.paper,
            zIndex: theme.zIndex.drawer - 1,
          })}
        >
          {!error && <CircularProgress size={150} />}
          <Typography sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
            {error ? error : "Loading..."}
          </Typography>
        </Box>
      </Stack>
    );
  }

  if (!isAllowed) {
    return <Navigate state={{ from: location }} to="/" replace />;
  }

  return children ? children : <Outlet />;
};

export default ProtectedRoute;
